import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Highlighter, Trash2, BookOpen, ArrowRight, Loader2, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { getSurlignages, deleteSurlignage } from "../lib/supabase";

const colorClasses = {
  yellow: "bg-yellow-200/50 border-yellow-300",
  green: "bg-green-200/50 border-green-300",
  blue: "bg-blue-200/50 border-blue-300",
  pink: "bg-pink-200/50 border-pink-300",
  orange: "bg-orange-200/50 border-orange-300",
};

const colorLabels = {
  yellow: "Jaune",
  green: "Vert",
  blue: "Bleu",
  pink: "Rose",
  orange: "Orange",
};

const bookNames = {
  genese: "Genèse", exode: "Exode", levitique: "Lévitique", nombres: "Nombres",
  deuteronome: "Deutéronome", josue: "Josué", juges: "Juges", ruth: "Ruth",
  samuel1: "1 Samuel", samuel2: "2 Samuel", rois1: "1 Rois", rois2: "2 Rois",
  chroniques1: "1 Chroniques", chroniques2: "2 Chroniques", esdras: "Esdras",
  nehemie: "Néhémie", tobie: "Tobie", judith: "Judith", esther: "Esther",
  maccabees1: "1 Maccabées", maccabees2: "2 Maccabées", job: "Job",
  psaumes: "Psaumes", proverbes: "Proverbes", ecclesiaste: "Ecclésiaste",
  cantique: "Cantique", sagesse: "Sagesse", siracide: "Siracide",
  isaie: "Isaïe", jeremie: "Jérémie", lamentations: "Lamentations",
  baruch: "Baruch", ezechiel: "Ézéchiel", daniel: "Daniel", osee: "Osée",
  joel: "Joël", amos: "Amos", abdias: "Abdias", jonas: "Jonas", michee: "Michée",
  nahum: "Nahum", habacuc: "Habacuc", sophonie: "Sophonie", aggee: "Aggée",
  zacharie: "Zacharie", malachie: "Malachie",
  matthieu: "Matthieu", marc: "Marc", luc: "Luc", jean: "Jean",
  actes: "Actes", romains: "Romains", corinthiens1: "1 Corinthiens",
  corinthiens2: "2 Corinthiens", galates: "Galates", ephesiens: "Éphésiens",
  philippiens: "Philippiens", colossiens: "Colossiens",
  thessaloniciens1: "1 Thessaloniciens", thessaloniciens2: "2 Thessaloniciens",
  timothee1: "1 Timothée", timothee2: "2 Timothée", tite: "Tite",
  philemon: "Philémon", hebreux: "Hébreux", jacques: "Jacques",
  pierre1: "1 Pierre", pierre2: "2 Pierre", jean1: "1 Jean", jean2: "2 Jean",
  jean3: "3 Jean", jude: "Jude", apocalypse: "Apocalypse"
};

const SurlignagesPage = () => {
  const [surlignages, setSurlignages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();
  
  // User ID - strictly use Supabase auth UUID (no fallback)
  const userId = user?.id || null;

  useEffect(() => {
    if (isPremium && userId) {
      fetchSurlignages();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, userId]);

  const fetchSurlignages = async () => {
    try {
      console.log('Fetching surlignages for userId:', userId);
      const data = await getSurlignages(userId);
      console.log('Surlignages fetched:', data);
      setSurlignages(data || []);
    } catch (error) {
      console.error("Error fetching surlignages:", error);
      toast.error("Erreur lors du chargement des surlignages");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      console.log('Attempting to delete surlignage:', deleteId);
      const result = await deleteSurlignage(deleteId);
      console.log('Delete result:', result);
      setSurlignages(prev => prev.filter(s => s.id !== deleteId));
      toast.success("Surlignage supprimé");
    } catch (error) {
      console.error("Error deleting surlignage:", error);
      toast.error("Erreur lors de la suppression : " + (error.message || "Erreur inconnue"));
    } finally {
      setDeleteId(null);
    }
  };

  const navigateToVerse = (s) => {
    navigate(`/bible/${s.book_id}/${s.chapter}`, {
      state: { scrollToVerse: s.verse_start }
    });
  };

  const formatReference = (s) => {
    const bookName = bookNames[s.book_id] || s.book_id;
    if (s.verse_end && s.verse_end !== s.verse_start) {
      return `${bookName} ${s.chapter}:${s.verse_start}-${s.verse_end}`;
    }
    return `${bookName} ${s.chapter}:${s.verse_start}`;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
        <p className="text-ink-secondary">Chargement...</p>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-lg text-center">
        <Crown className="h-16 w-16 text-gold-leaf mx-auto mb-6" />
        <h1 className="font-heading text-2xl text-ink-primary mb-4">Fonctionnalité Premium</h1>
        <p className="text-ink-secondary mb-6">
          Les surlignages sont réservés aux utilisateurs Premium.
        </p>
        <Button onClick={() => navigate("/premium")} className="bg-gold-leaf hover:bg-gold-leaf/90">
          Passer Premium
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Highlighter className="h-7 w-7 text-rubric-red" />
          <h1 className="font-heading text-2xl text-ink-primary">Mes surlignages</h1>
        </div>
        <p className="text-ink-secondary text-sm">{surlignages.length} passage(s) surligné(s)</p>
      </div>

      {surlignages.length === 0 ? (
        <div className="text-center py-12">
          <Highlighter className="h-12 w-12 text-ink-secondary/30 mx-auto mb-4" />
          <p className="text-ink-secondary mb-4">Aucun surlignage pour le moment</p>
          <Button variant="outline" onClick={() => navigate("/bible/genese/1")}>
            <BookOpen className="h-4 w-4 mr-2" />
            Commencer à lire
          </Button>
        </div>
      ) : (
        <ScrollArea className="h-[calc(100vh-220px)]">
          <div className="space-y-3">
            {surlignages.map((s) => (
              <Card key={s.id} className={`border-l-4 ${colorClasses[s.color] || colorClasses.yellow}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => navigateToVerse(s)}
                        className="text-left w-full group"
                      >
                        <p className="font-heading text-ink-primary group-hover:text-rubric-red transition-colors">
                          {formatReference(s)}
                        </p>
                        <p className="text-sm text-ink-secondary line-clamp-2 mt-1">
                          {s.text || "..."}
                        </p>
                      </button>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {colorLabels[s.color] || s.color}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigateToVerse(s)}
                        className="h-8 w-8"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(s.id)}
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce surlignage ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SurlignagesPage;
